import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useAddApiTokenMutation } from "modules/settings/hooks/useAddApiTokenMutation"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ApiTokenAccessEnum, CreateApiTokenSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

type FieldValues = z.infer<typeof CreateApiTokenSchema>

export function CreateApiToken() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync, isPending } = useAddApiTokenMutation()
  const form = useForm<FieldValues>({
    resolver: zodResolver(CreateApiTokenSchema),
    defaultValues: {
      name: "",
      access: ApiTokenAccessEnum.Full,
    },
  })

  function onClose() {
    navigate({ to: "/settings/keys-and-secrets" })
  }

  async function onSubmit(data: FieldValues) {
    await mutateAsync(data)
    toast.success(t("settings.keysAndSecrets.apiTokens.addSuccess"))
    onClose()
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[475px]">
        <DialogHeader>
          <DialogTitle>{t("settings.keysAndSecrets.apiTokens.create.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.keysAndSecrets.apiTokens.create.description")}
          </DialogDescription>
        </DialogHeader>
        <Form
          {...form}
          errorTranslations={getErrorTranslations()}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.keysAndSecrets.apiTokens.create.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="access"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("settings.keysAndSecrets.apiTokens.create.access")}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ApiTokenAccessEnum.Full}>
                          {t("settings.keysAndSecrets.apiTokens.access.full")}
                        </SelectItem>
                        <SelectItem value={ApiTokenAccessEnum.Read}>
                          {t("settings.keysAndSecrets.apiTokens.access.read")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("shared.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("settings.keysAndSecrets.apiTokens.create.submit")}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
